import { LinearInterpolate } from './LinearInterpolation';
import { CentrelinkStaticData, ChildCalculationResult, ChildcareCentrePricingPoint, EnrolledChild, FamilyDetails, PointAdapter, EnrolledChildWithHours } from './types';

export class SubsidyCalculator {
    private sessionHourOptions = [ 8, 9, 10, 11 ];

    constructor(private staticData: CentrelinkStaticData) {
    }

    public calculateSubsidyRate(combinedFamilyIncome: number) : number {
        return this.findPointOnCurve(combinedFamilyIncome, this.staticData.incomeSubsidyCurve, true, p => ({x: p.combinedAnnualIncome, y: p.subsidyPercentage})) || 0;
    }

    public calculateMaximumSubsidisedHoursPerChild(hoursOfRecognisedActivity: number, combinedFamilyIncome: number, exemptFromActivityTest: boolean): number {
        const standardAllocation = this.findPointOnCurve(hoursOfRecognisedActivity, this.staticData.subsidisedHoursEntitlement.activityHoursSubsidisedHoursCurve, false, p => ({x: p.activityHours, y: p.subsidisedHours})) || 0;

        if (combinedFamilyIncome <= this.staticData.subsidisedHoursEntitlement.lowIncomeFamilyIncomeThreshold) {
            return Math.max(standardAllocation, this.staticData.subsidisedHoursEntitlement.lowIncomeFamilySubsidisedHoursEntitlement);
        }

        return standardAllocation;
    }

    public calculateDailyRateForChild(ageInMonths: number, pricing: ChildcareCentrePricingPoint[]): number | undefined {
        return this.findPointOnCurve(ageInMonths, pricing, false, p => ({x: p.ageOfChildInMonths, y: p.dailyRate }));
    }

    public calculateEnrolledChild(enrolledChild: EnrolledChildWithHours, familyDetails: FamilyDetails, pricing: ChildcareCentrePricingPoint[]): ChildCalculationResult {
        const careHoursPerFortnight: number = enrolledChild.attendHoursPerDay * enrolledChild.attendSessionsPerWeek * 2;

        const fullDayDate = this.calculateDailyRateForChild(enrolledChild.ageInMonths, pricing);
        if (!fullDayDate) {
            return {
                enrolledChild,
                estimate: undefined
            };
        }

        const hourlyRate = fullDayDate / enrolledChild.attendHoursPerDay;
        let hourlyRateCap: number;
        const hourlyRateCaps = this.staticData.daycarePricingCaps.centreBasedDaycare;
        if (enrolledChild.ageInMonths > 12 * 6) {
            hourlyRateCap = hourlyRateCaps.schoolAgedChildren;
        } else {
            hourlyRateCap = hourlyRateCaps.childrenBelowSchoolAge;
        }



        let hoursEligableForSubsidyPerFortnight: number;
        let subsidyPercentage: number;


        if (familyDetails.knowSubsidyDetails) {
            hoursEligableForSubsidyPerFortnight = familyDetails.hoursEligableForSubsidyPerFortnight!;
            subsidyPercentage = familyDetails.subsidyPercentage!;
        } else {
            const activityHoursPerFortnight = familyDetails.activityHoursPerFortnight || 0;
            hoursEligableForSubsidyPerFortnight = this.calculateMaximumSubsidisedHoursPerChild(activityHoursPerFortnight, familyDetails.combinedTaxableIncome!, false);
            subsidyPercentage = this.calculateSubsidyRate(familyDetails.combinedTaxableIncome!);
        }

        const unsubsidisedHoursPerFortnight =  Math.max(0, careHoursPerFortnight - hoursEligableForSubsidyPerFortnight)
        const subsidisedHoursPerFortnight = careHoursPerFortnight - unsubsidisedHoursPerFortnight;
        const cappedHourlyRate = Math.min(hourlyRate, hourlyRateCap);
        const totalFortnightlyCost = hourlyRate * careHoursPerFortnight;

        let subsidisedFortnightlyCost = subsidisedHoursPerFortnight * cappedHourlyRate * subsidyPercentage;

        const subsidisedCostWithheldPerFortnight = familyDetails.subsidyWitholdingPercentage * subsidisedFortnightlyCost;
        const outOfPocketPerFornight = totalFortnightlyCost - subsidisedFortnightlyCost + subsidisedCostWithheldPerFortnight;

        const eligableForLowActivityBenefit = enrolledChild.ageInMonths >= (3 * 12) && hoursEligableForSubsidyPerFortnight < 36 && subsidyPercentage > 0;

        const estimate = {
            careHoursPerFortnight,
            hourlyRate,
            cappedHourlyRate,
            totalFortnightlyCost,
            hoursEligableForSubsidyPerFortnight,
            unsubsidisedHoursPerFortnight,
            subsidisedHoursPerFortnight,
            subsidyPercentage,
            subsidisedFortnightlyCost,
            subsidisedCostWithheldPerFortnight,
            outOfPocketPerFornight,
            eligableForLowActivityBenefit
        }

        return {
            enrolledChild,
            estimate
        };
    }

    public calculateEnrolledChildMinimizeGapMaximizeHours(enrolledChild: EnrolledChild, familyDetails: FamilyDetails, pricing: ChildcareCentrePricingPoint[]): ChildCalculationResult {
        const results = this.sessionHourOptions.map(attendHours => {
            const enrolledChildWithHours: EnrolledChildWithHours = {
                ...enrolledChild,
                attendHoursPerDay: attendHours
            };

            return this.calculateEnrolledChild(enrolledChildWithHours, familyDetails, pricing);
        });

        return results.reduce((prev, current) => {
            if (current.estimate!.outOfPocketPerFornight <= prev.estimate!.outOfPocketPerFornight && current.enrolledChild.attendHoursPerDay >= prev.enrolledChild.attendHoursPerDay) {
                return current;
            }

            return prev;
        }, results[0]);
    }

    private findPointOnCurve<T>(x: number, points: T[], interpolate: boolean, pointAdapter: PointAdapter<T>): number | undefined {
        const curve = points.map(pointAdapter);

        const exactPoint = curve.find(p => p.x === x);
        if (exactPoint) {
            return exactPoint.y;
        }

        // Find the last point less than the requested
        const pointsReversed = curve.slice(0).reverse();
        const pointA = pointsReversed.find(p => p.x < x);

        // Find the first point greater than the requested
        const pointB = curve.find(p => p.x > x);

        if (!pointA || !pointB) {
            return undefined;
        }

        if (interpolate) {
            return LinearInterpolate(pointA, pointB, x).y;
        } else {
            // Only return a value if the gradient is flat
            if (pointA.y === pointB.y) {
                return pointA.y;
            }

            return undefined;
        }
    }
}